import React from "react";
import { usePageMeta } from "@/lib/seo";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const steps = [
  { n: "01", t: "Consulenza gratuita", d: "Ci racconti la tua situazione: tipologia di debiti, importi indicativi, urgenze (pignoramenti, aste, decreti ingiuntivi). Analisi iniziale a costo zero, nessun impegno." },
  { n: "02", t: "Delega e Protezione", d: "Con il mandato firmato prendiamo in carico ogni comunicazione con banche, servicer, finanziarie e agenzie di riscossione. Da questo momento non parli più da solo con nessun creditore." },
  { n: "03", t: "Studio Tecnico", d: "Analizziamo i contratti documento per documento: prescrizioni, illegittimità, tassi usurari, conteggi post-vendita. Costruiamo la mappa dei debiti reali, spesso molto più bassa di quella richiesta." },
  { n: "04", t: "Strategia e Chiusura", d: "Impostiamo la strategia di saldo e stralcio caso per caso e trattiamo fino all'accordo firmato. Il compenso ti viene richiesto solo alla chiusura effettiva, come percentuale sul risparmio ottenuto." },
];

export default function Method() {
  usePageMeta(
    "Il Metodo — Come funziona il Saldo e Stralcio | Sdebitiamoci Srl",
    "Analisi gratuita, delega, trattativa e chiusura a stralcio: il nostro metodo passo per passo per ridurre i tuoi debiti dal 50% al 70%."
  );
  return (
    <div className="pt-32 pb-24" data-testid="method-page">
      <section className="container-editorial">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Il nostro metodo</p>
        <h1 className="font-serif text-5xl md:text-[6rem] leading-[0.88] tracking-tight mt-6 text-[#2d6fc4]">
          Quattro passi.<br/><em className="italic text-[#2d6fc4]">Un solo obiettivo:</em><br/>chiudere il debito.
        </h1>
        <p className="mt-10 max-w-2xl text-lg text-[#333333]">
          Il saldo e stralcio è un accordo transattivo tra debitore e creditore: si chiude la posizione pagando una cifra inferiore al dovuto. Funziona quando è preparato con metodo. Ecco il nostro.
        </p>
      </section>

      <section className="container-editorial mt-24 space-y-24">
        {steps.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22,1,0.36,1] }}
            className="grid grid-cols-12 gap-6 border-t border-[#E5E7EB] pt-16"
          >
            <div className="col-span-12 md:col-span-4">
              <p className="chapter-num text-[#2d6fc4] text-[8rem] leading-none">{s.n}</p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2d6fc4]">{s.t}</h2>
              <p className="mt-6 text-lg text-[#333333] leading-relaxed max-w-2xl">{s.d}</p>
            </div>
          </motion.div>
        ))}
      </section>

      <section className="container-editorial mt-32" data-testid="faq-section">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— FAQ</p>
        <h2 className="font-serif text-4xl md:text-6xl leading-tight mt-4 text-[#2d6fc4] max-w-4xl">
          Domande frequenti su <em className="italic text-[#2d6fc4]">pignoramento e debito residuo</em>.
        </h2>
        <div className="mt-12 divide-y divide-[#E5E7EB] border-y border-[#E5E7EB]" itemScope itemType="https://schema.org/FAQPage">
          {[
            [
              "Come si ferma un pignoramento immobiliare in corso?",
              "Il pignoramento si può sospendere presentando una proposta transattiva formale e documentata al creditore procedente, dimostrando disponibilità economica reale (spesso tramite un terzo finanziatore) e serietà della trattativa. Nei casi in cui l'asta è già fissata, si può chiedere la sospensione dell'esecuzione ex art. 624 c.p.c. o l'accoglimento di un'istanza di conversione. Ogni sospensione richiede tempi rapidi: prima si interviene, maggiore è la possibilità di fermare la vendita."
            ],
            [
              "Cos'è il debito residuo dopo l'asta e come si abbatte?",
              "Il debito residuo è la somma che il debitore continua a dovere al creditore anche dopo la vendita all'asta dell'immobile, quando il ricavato non copre l'intero importo iscritto a ruolo. Su questi importi è possibile aprire una trattativa di saldo e stralcio a valore molto ridotto, perché il creditore ha già incassato il grosso e sa che il residuo è di difficile recupero. Nei nostri casi il debito residuo viene abbattuto in media dell'80–95%."
            ],
            [
              "Il saldo e stralcio è possibile anche con l'Agenzia delle Entrate Riscossione?",
              "Sì. Le cartelle esattoriali possono essere ridotte attraverso definizioni agevolate (rottamazioni, saldo e stralcio previsti da legge) oppure tramite verifica delle prescrizioni: molte partite iscritte a ruolo sono estinte per decorso del termine di 10 o 5 anni. Un'analisi documentale puntuale spesso porta ad abbattimenti significativi anche senza norme di condono."
            ],
            [
              "Quanto costa affidarsi a Sdebitiamoci Srl?",
              "La consulenza iniziale, l'analisi documentale e la trattativa con i creditori sono totalmente gratuite. Nessun anticipo, nessuna spesa amministrativa. Il compenso viene richiesto esclusivamente al perfezionamento dell'accordo di saldo e stralcio, come percentuale sul risparmio effettivamente ottenuto. Se non raggiungiamo un accordo utile, non paghi nulla."
            ],
            [
              "Posso essere pignorato sullo stipendio durante la trattativa?",
              "Il pignoramento presso terzi sullo stipendio o sulla pensione è uno degli strumenti più utilizzati dai creditori. Con una proposta transattiva ben formulata si può ottenere la sospensione o la riduzione della trattenuta, soprattutto quando si dimostra impossibilità economica oggettiva. Nei casi più urgenti si procede in parallelo con opposizione tempestiva davanti al giudice dell'esecuzione."
            ],
            [
              "Quanto dura mediamente una trattativa di saldo e stralcio?",
              "Dipende dal tipo di creditore e dal numero di posizioni: le finanziarie e i servicer chiudono in 3–6 mesi, le banche 6–12 mesi, l'Agenzia delle Entrate 8–14 mesi. Nei nostri casi tipici (Sumudu 8 mesi, Bergamo 5 mesi, Alessandro 10 mesi) i tempi si sono mantenuti coerenti con queste medie."
            ],
          ].map(([q, a], i) => (
            <details key={q} className="py-6 group" itemScope itemProp="mainEntity" itemType="https://schema.org/Question" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                <h3 className="font-serif text-xl md:text-2xl text-[#2d6fc4] leading-snug" itemProp="name">{q}</h3>
                <span className="mt-1 shrink-0 h-6 w-6 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#2d6fc4] group-open:rotate-45" style={{ transition: "transform 200ms ease" }}>+</span>
              </summary>
              <div className="mt-4 max-w-3xl text-[#333333] leading-relaxed" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p itemProp="text">{a}</p>
              </div>
            </details>
          ))}
        </div>
      </section>

      <section className="container-editorial mt-24">
        <div className="bg-[#1a4a8a] text-[#FFFFFF] rounded-lg p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <p className="font-serif text-3xl md:text-4xl max-w-xl leading-tight">
            Vuoi capire se il tuo caso è trattabile? <span className="italic text-white">Consulenza conoscitiva: contattaci senza impegno.</span>
          </p>
          <Link to="/contatti" className="btn-cta inline-flex items-center gap-2 px-7 py-4 rounded-lg text-sm">
            Prenota la tua analisi gratuita <ArrowUpRight size={16}/>
          </Link>
        </div>
      </section>
    </div>
  );
}
